import React, { createContext, useCallback, useContext, useState } from "react";
import {
    AuditLogPagination,
    AuditLogQuery,
    AuditLogRecord,
    AuditLogSummaryItem,
    emptyAuditPagination,
} from "../types/auditLog";
import { getAuditLogById, getAuditLogs, getAuditLogSummary } from "../api/auditLogApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface AuditLogContextProps {
    logs: AuditLogRecord[];
    pagination: AuditLogPagination;
    summary: AuditLogSummaryItem[];
    loading: boolean;
    fetchLogs: (query?: AuditLogQuery) => Promise<void>;
    fetchSummary: (query?: AuditLogQuery) => Promise<void>;
    fetchLogById: (id: string) => Promise<AuditLogRecord | null>;
}

const AuditLogContext = createContext<AuditLogContextProps | undefined>(undefined);

export const AuditLogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [logs, setLogs] = useState<AuditLogRecord[]>([]);
    const [pagination, setPagination] = useState<AuditLogPagination>(emptyAuditPagination);
    const [summary, setSummary] = useState<AuditLogSummaryItem[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchLogs = useCallback(async (query: AuditLogQuery = {}) => {
        setLoading(true);
        const result = await handleWithAlert(
            () => getAuditLogs(query),
            { errorMessage: "Failed to fetch audit logs" }
        );
        if (result) {
            setLogs(result.data.map((log: any) => ({ ...log, id: log.id || log._id })));
            setPagination(result.pagination);
        }
        setLoading(false);
    }, []);

    // Drives the model filter options, so a failure here must not block the list.
    const fetchSummary = useCallback(async (query: AuditLogQuery = {}) => {
        try {
            setSummary(await getAuditLogSummary(query));
        } catch {
            setSummary([]);
        }
    }, []);

    const fetchLogById = useCallback(async (id: string) => {
        return handleWithAlert(
            () => getAuditLogById(id),
            { errorMessage: "Failed to fetch audit log details" }
        );
    }, []);

    return (
        <AuditLogContext.Provider value={{
            logs,
            pagination,
            summary,
            loading,
            fetchLogs,
            fetchSummary,
            fetchLogById
        }}>
            {children}
        </AuditLogContext.Provider>
    );
};

export const useAuditLog = () => {
    const ctx = useContext(AuditLogContext);
    if (!ctx) throw new Error("useAuditLog must be inside AuditLogProvider");
    return ctx;
};
